
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { auth } from "@/shared/api/graphqlClient";
import { LanguageProvider, useLanguage } from "@/shared/components/shared/LanguageContext";
import LanguageSwitcher from "@/shared/components/shared/LanguageSwitcher";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { canAccessSection, isStaffUser, getUserPermissions } from "@/shared/utils/permissions";
import {
  LayoutDashboard,
  ArrowDownToLine,
  ArrowUpFromLine,
  Building2,
  Server,
  Route,
  Webhook,
  FileText,
  Settings,
  ChevronLeft,
  ChevronRight,
  Bell,
  Search,
  User,
  LogOut,
  Shield,
  Activity,
  Menu,
  X,
  Users,
  Zap,
  Key,
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Badge } from "@/shared/components/ui/badge";
import NotificationDropdown from "@/shared/components/shared/NotificationDropdown";
import useInfrastructureStatus from "@/shared/hooks/useInfrastructureStatus";

import { Globe, Cpu } from "lucide-react";

const getAdminNavItems = (t) => [
  { name: t("dashboard"), icon: LayoutDashboard, page: "AdminDashboard", permission: null }, // Always visible
  { name: t("allTransactions"), icon: Globe, page: "AdminGlobalTransactions", permission: "transactions.view" },
  { name: t("transactions"), icon: Activity, page: "Transactions", permission: "transactions.view" },
  { name: t("deposits"), icon: ArrowDownToLine, page: "Deposits", permission: "transactions.view" },
  { name: t("withdrawals"), icon: ArrowUpFromLine, page: "Withdrawals", permission: "transactions.view" },
  { name: t("merchants"), icon: Building2, page: "Merchants", permission: "merchants.view" },
  { name: t("engines"), icon: Zap, page: "AdminEngines", permission: "engines.view" },
  { name: t("resellers"), icon: Users, page: "AdminResellers", permission: "resellers.view" },
  { name: t("staff"), icon: Shield, page: "AdminStaff", permission: "staff.view" },
  { name: t("roles"), icon: Key, page: "AdminRoles", permission: "roles.view" },
  // These sections are visible to main entities but hidden from staff unless they have permission
  // Use null permission for main entities, but staff will need explicit permission
  { name: t("providers"), icon: Server, page: "Providers", permission: null, staffPermission: "providers.view" },
  { name: t("routing"), icon: Route, page: "Routing", permission: null, staffPermission: "routing.view" },
  { name: t("webhooks"), icon: Webhook, page: "WebhookLogs", permission: null, staffPermission: "webhooks.view" },
  { name: t("settlements"), icon: FileText, page: "Settlements", permission: null, staffPermission: "settlements.view" },
  { name: t("servers"), icon: Cpu, page: "AdminServers", permission: null, staffPermission: "servers.view" },
  { name: t("settings"), icon: Settings, page: "Settings", permission: null, staffPermission: "settings.view" }, // Visible to main entities, requires settings.view for staff
];

const getMerchantNavItems = (t) => [
  { name: t("dashboard"), icon: LayoutDashboard, page: "MerchantDashboard" },
  { name: t("transactions"), icon: Activity, page: "MerchantTransactions" },
  { name: t("deposits"), icon: ArrowDownToLine, page: "MerchantDeposit" },
  { name: t("withdrawals"), icon: ArrowUpFromLine, page: "MerchantWithdrawals" },
  { name: t("settings"), icon: Settings, page: "MerchantSettings" },
];

// Pages that use their own layout and should bypass the default layout
const customLayoutPages = [
  "AdminLogin",
  "MerchantDashboard",
  "MerchantTransactions", 
  "MerchantDeposit",
  "MerchantWithdrawals",
  "MerchantProviders",
  "MerchantRouting",
  "MerchantWebhooks",
  "MerchantSettlements",
  "MerchantSettings",
  "MerchantLogin",
  "ResellerDashboard",
  "ResellerTransactions",
  "ResellerDeposits",
  "ResellerWithdrawals",
  "ResellerMerchants",
  "ResellerProviders",
  "ResellerRouting",
  "ResellerWebhooks",
  "ResellerSettlements",
  "ResellerSettings",
  "ResellerLogin",
  "EngineDashboard",
  "EngineTransactions",
  "EngineDeposits",
  "EngineWithdrawals",
  "EngineMerchants",
  "EngineResellers",
  "EngineProviders",
  "EngineRouting",
  "EngineWebhooks",
  "EngineSettlements",
  "EngineSettings",
  "EngineLogin",
];

function LayoutContent({ children, currentPageName }) {
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [user, setUser] = useState(null);
    const [isAdmin, setIsAdmin] = useState(true);
    const { data, loading, error } = useInfrastructureStatus();
    const { t } = useLanguage();
    const { hasPermission } = usePermissions();

    useEffect(() => {
      const loadUser = async () => {
        try {
          const currentUser = await auth.me();
          setUser(currentUser);
          const userRole = auth.getUserRole();
          setIsAdmin(userRole?.toUpperCase() === 'ADMIN');
        } catch (e) {
          console.log('User not logged in');
        }
      };
      loadUser();
    }, []);

    // Filter nav items based on permissions
    const allNavItems = isAdmin ? getAdminNavItems(t) : getMerchantNavItems(t);
    const isStaff = isStaffUser();
    const userPermissions = getUserPermissions();
    
    // Debug logging in development
    if (import.meta.env.DEV) {
      console.log('Sidebar filtering:', {
        isStaff,
        isAdmin,
        userPermissions,
        totalNavItems: allNavItems.length,
      });
    }
    
    const navItems = allNavItems.filter(item => {
      // For staff users, use staffPermission if available, otherwise use permission
      if (isStaff) {
        // Staff users must have explicit permission for each item
        const requiredPermission = item.staffPermission || item.permission;
        
        if (!requiredPermission) {
          // No permission required - hide from staff (they need explicit permissions)
          if (import.meta.env.DEV) {
            console.log(`Hiding ${item.name} from staff (no permission requirement)`);
          }
          return false;
        }
        
        const hasAccess = hasPermission(requiredPermission);
        
        if (import.meta.env.DEV) {
          console.log(`${item.name}: ${hasAccess ? 'SHOW' : 'HIDE'} (requires: ${requiredPermission})`);
        }
        
        return hasAccess;
      }
      
      // For main entities (Admin, Engine, etc.), show items without permission requirement
      // But still check permissions if specified
      if (!item.permission) {
        // No permission required - always show for main entities
        return true;
      }
      
      // Check permission if specified (for main entities, this should always pass)
      const hasAccess = hasPermission(item.permission);
      
      if (import.meta.env.DEV && !hasAccess) {
        console.warn(`Main entity missing permission ${item.permission} for ${item.name}`);
      }
      
      return hasAccess;
    });
    
    if (import.meta.env.DEV) {
      console.log('Filtered nav items:', {
        before: allNavItems.length,
        after: navItems.length,
        visible: navItems.map(i => i.name),
      });
    }

    const handleLogout = () => {
      const userRole = auth.getUserRole();
      auth.logout();
      
      // Redirect to appropriate login page based on role
      const loginPages = {
        'ADMIN': '/AdminLogin',
        'ENGINE': '/EngineLogin',
        'RESELLER': '/ResellerLogin',
        'MERCHANT': '/MerchantLogin',
      };
      
      window.location.href = loginPages[userRole?.toUpperCase()] || '/AdminLogin';
    };

    const infraStatus = data?.infrastructure;
    const apiStatus = data?.apiGateway;
    const dbStatus = data?.database;

    const getDotColor = (status) => {
      if (status === 'UP') return 'bg-emerald-400';
      if (status === 'DOWN') return 'bg-rose-400';
      return 'bg-slate-500';
    };

    const getTextColor = (status) => {
      if (status === 'UP') return 'text-emerald-400';
      if (status === 'DOWN') return 'text-rose-400';
      return 'text-slate-400';
    };

    const formatLatency = (latency) =>
      latency === null || latency === undefined ? '--' : `${latency}ms`;

    const overallStatusLabel = loading
      ? 'Checking...'
      : error
        ? 'Unavailable'
        : `${infraStatus?.uptimePercentage ?? '--'}% ${infraStatus?.status ?? ''}`;

    // If this page has its own layout, just render children without the default layout
    if (customLayoutPages.includes(currentPageName)) {
      return <>{children}</>;
    }

    return (
    <div className="min-h-screen bg-slate-50">
      <style>{`
        :root {
          --primary: 222.2 47.4% 11.2%;
          --primary-foreground: 210 40% 98%;
          --accent: 217.2 91.2% 59.8%;
          --accent-foreground: 222.2 47.4% 11.2%;
        }
        
        .nav-item {
          transition: all 0.2s ease;
        }
        
        .nav-item:hover {
          transform: translateX(4px);
        }
        
        .nav-item.active {
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }
        
        .glass-card {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.5);
        }
        
        .sidebar {
                    background: linear-gradient(180deg, #0f172a 0%, #1e293b 100%);
                  }

                  .sidebar::-webkit-scrollbar,
                  .sidebar nav::-webkit-scrollbar {
                    width: 6px;
                  }

                  .sidebar::-webkit-scrollbar-track,
                  .sidebar nav::-webkit-scrollbar-track {
                    background: transparent;
                  }

                  .sidebar::-webkit-scrollbar-thumb,
                  .sidebar nav::-webkit-scrollbar-thumb {
                    background: rgba(148, 163, 184, 0.3);
                    border-radius: 3px;
                  }

                  .sidebar::-webkit-scrollbar-thumb:hover,
                  .sidebar nav::-webkit-scrollbar-thumb:hover {
                    background: rgba(148, 163, 184, 0.5);
                  }
        
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        .pulse-dot {
          animation: pulse-dot 2s ease-in-out infinite;
        }
      `}</style>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-slate-900 z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            className="text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
              <Shield className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold text-white">Demo Gateway</span>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`sidebar fixed top-0 left-0 h-screen z-50 transition-all duration-300 ease-in-out flex flex-col
          ${collapsed ? 'w-20' : 'w-64'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className={`h-16 flex items-center border-b border-slate-700 flex-shrink-0 ${collapsed ? 'justify-center px-2' : 'px-6'}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
              <Shield className="h-5 w-5 text-white" />
            </div>
            {!collapsed && (
              <div>
                <h1 className="font-bold text-white text-lg tracking-tight">Demo Gateway</h1>
                                      <p className="text-[10px] text-slate-400 uppercase tracking-wider">Payment Gateway</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 flex-1 overflow-y-auto">
          {!collapsed && (
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-3 px-3">
              {isAdmin ? t('adminPanel') : t('merchantPortal')}
            </p>
          )}

          {navItems.map((item) => {
            const isActive = currentPageName === item.page;
            return (
              <Link
                key={item.page}
                to={createPageUrl(item.page)}
                onClick={() => setMobileOpen(false)}
                className={`nav-item flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                  ${isActive 
                    ? 'active text-white' 
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }
                  ${collapsed ? 'justify-center' : ''}
                `}
              >
                <item.icon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-white' : ''}`} />
                {!collapsed && <span>{item.name}</span>}
              </Link>
            );
          })}
        </nav>

        {/* System Status */}
        {!collapsed && (
          <div className="mt-auto px-4 pb-16">
            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${getDotColor(infraStatus?.status)} pulse-dot`} />
                  <span className="text-xs text-slate-400 font-medium">{t('infrastructure')}</span>
                </div>
                <span className={`text-[10px] font-medium ${getTextColor(infraStatus?.status)}`}>
                  {overallStatusLabel}
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">{t('apiGateway')}</span>
                  <div className="flex items-center gap-1">
                    <div className={`w-1.5 h-1.5 rounded-full ${getDotColor(apiStatus?.status)}`} />
                    <span className={`text-xs ${getTextColor(apiStatus?.status)}`}>
                      {loading ? '--' : formatLatency(apiStatus?.latencyMs)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">{t('database')}</span>
                  <div className="flex items-center gap-1">
                    <div className={`w-1.5 h-1.5 rounded-full ${getDotColor(dbStatus?.status)}`} />
                    <span className={`text-xs ${getTextColor(dbStatus?.status)}`}>
                      {loading ? '--' : formatLatency(dbStatus?.latencyMs)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Collapse Button */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex absolute bottom-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-slate-700 items-center justify-center text-slate-400 hover:text-white hover:bg-slate-600 transition-colors"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${collapsed ? 'lg:pl-20' : 'lg:pl-64'}`}>
        {/* Top Bar */}
        <header className="sticky top-0 z-30 hidden lg:flex h-16 items-center justify-between px-6 bg-white/80 backdrop-blur-lg border-b border-slate-200">
          <div className="flex items-center gap-4 flex-1">
            <div className="relative w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input 
                placeholder={t("searchHeaderPlaceholder")} 
                className="pl-10 bg-slate-50 border-slate-200 focus:bg-white"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
                    <LanguageSwitcher variant="ghost" />
                    <NotificationDropdown accentColor="blue" />

                    <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 pl-2 pr-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                    <User className="h-4 w-4 text-white" />
                  </div>
                  <div className="text-left hidden sm:block">
                    <p className="text-sm font-medium">{user?.full_name || 'Admin'}</p>
                    <p className="text-xs text-slate-500">{isAdmin ? 'Administrator' : 'Merchant'}</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2 border-b">
                  <p className="text-sm font-medium">{user?.full_name || 'Admin'}</p>
                  <p className="text-xs text-slate-500">{user?.email || 'Administrator'}</p>
                </div>
                <Link to={createPageUrl("Settings")}>
                  <DropdownMenuItem>
                    <Settings className="h-4 w-4 mr-2" />
                    {t('settings')}
                  </DropdownMenuItem>
                </Link>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="h-4 w-4 mr-2" />
                  {t('logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6 pt-20 lg:pt-6 min-h-[calc(100vh-4rem)]">
          {children}
        </main>
      </div>
    </div>
  );
}

export default function Layout({ children, currentPageName }) {
  return (
    <LanguageProvider>
      <LayoutContent currentPageName={currentPageName}>{children}</LayoutContent>
    </LanguageProvider>
  );
}
